import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Body1,
  Body2,
  Caption1,
  Badge,
  Avatar,
  Button,
  Dropdown,
  Option,
  Input,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tab,
  TabList,
  Divider,
  Tooltip,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  Search24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  History24Regular,
} from '@fluentui/react-icons';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Employee,
  Application,
  AppAssignment,
  ChangeLog,
  SPListItem,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { canManageAccess, canViewAllMembers } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import {
  formatCurrency,
  formatDateTime,
  getInitials,
} from '@/utils/formatters';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sidebar: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    padding: '12px',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto' as const,
  },
  memberCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background-color 0.1s',
    marginBottom: '4px',
  },
  memberCardActive: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  main: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    padding: '24px',
    minHeight: '400px',
  },
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
  },
  accessGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
    marginTop: '16px',
    alignItems: 'stretch',
  },
  appRow: {
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    minHeight: '140px',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  appRowAssigned: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #2e7d32',
  },
  appHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },
  appTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical' as const,
    lineHeight: '1.3',
    fontWeight: 600,
    wordBreak: 'break-word' as const,
  },
  appMeta: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },
  summaryCard: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  summaryValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
  },
  historyRow: {
    padding: '10px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
});

export function UserAccess() {
  const styles = useStyles();
  const { currentUser, listIds } = useAppContext();

  if (currentUser && !canViewAllMembers(currentUser.role)) {
    return <AccessDenied reason="User access management is restricted to Admin and Reporting Officers." />;
  }

  const canEdit = currentUser ? canManageAccess(currentUser.role) : false;

  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });
  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: !!listIds,
  });
  const {
    items: assignments,
    create: createAssignment,
    update: updateAssignment,
  } = useSharePointList<AppAssignment>('OC_AppAssignments', {
    autoFetch: !!listIds,
  });
  const { items: changeLog } = useSharePointList<ChangeLog>('OC_ChangeLog', {
    autoFetch: !!listIds,
    top: 500,
    orderBy: 'fields/Timestamp desc',
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [tab, setTab] = useState<'access' | 'history'>('access');
  const [updating, setUpdating] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const departments = useMemo(() => {
    const set = new Set<string>();
    for (const e of employees) if (e.fields.Department) set.add(e.fields.Department);
    return Array.from(set).sort();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    let res = employees.filter((e) => e.fields.Status === 'Active');
    if (deptFilter !== 'all') res = res.filter((e) => e.fields.Department === deptFilter);
    if (search) {
      const lower = search.toLowerCase();
      res = res.filter(
        (e) =>
          e.fields.Title.toLowerCase().includes(lower) ||
          e.fields.Email.toLowerCase().includes(lower),
      );
    }
    return res.sort((a, b) => a.fields.Title.localeCompare(b.fields.Title));
  }, [employees, deptFilter, search]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedId) ?? null,
    [employees, selectedId],
  );

  const userAssignments = useMemo(() => {
    if (!selectedEmployee) return [];
    const empId = Number(selectedEmployee.id);
    return assignments.filter((a) => a.fields.EmployeeId === empId);
  }, [assignments, selectedEmployee]);

  const assignmentMap = useMemo(() => {
    const map = new Map<number, SPListItem<AppAssignment>>();
    for (const a of userAssignments) {
      if (a.fields.Status === 'Active') {
        map.set(a.fields.AppId, a);
      }
    }
    return map;
  }, [userAssignments]);

  // Summary metrics for selected user
  const summary = useMemo(() => {
    const activeCount = assignmentMap.size;
    const totalCost = Array.from(assignmentMap.keys()).reduce((sum, appId) => {
      const app = apps.find((a) => Number(a.id) === appId);
      return sum + (app?.fields.MonthlyCostPerLicense ?? 0);
    }, 0);
    const revokedCount = userAssignments.filter(
      (a) => a.fields.Status === 'Revoked',
    ).length;
    return {
      activeCount,
      revokedCount,
      totalCost: Math.round(totalCost * 100) / 100,
      availableApps: apps.length - activeCount,
    };
  }, [userAssignments, apps, assignmentMap]);

  // Audit history for selected user
  const userHistory = useMemo(() => {
    if (!selectedEmployee) return [];
    const email = selectedEmployee.fields.Email.toLowerCase();
    return changeLog
      .filter(
        (l) =>
          l.fields.ChangedBy?.toLowerCase() === email ||
          l.fields.EntityName?.toLowerCase().includes(email) ||
          (l.fields.Entity === 'AppAssignments' &&
            l.fields.Details?.toLowerCase().includes(email)),
      )
      .slice(0, 50);
  }, [changeLog, selectedEmployee]);

  const handleToggle = async (appId: number, currentlyAssigned: boolean) => {
    if (!canEdit || !selectedEmployee) return;
    const key = `${appId}`;
    setUpdating(key);
    setMessage(null);
    try {
      const existing = userAssignments.find(
        (a) => a.fields.AppId === appId,
      );
      const app = apps.find((a) => Number(a.id) === appId);
      if (currentlyAssigned && existing) {
        await updateAssignment(existing.id, { Status: 'Revoked' });
        setMessage({ type: 'success', text: `Revoked access to ${app?.fields.Title}` });
      } else if (existing) {
        // Re-activate
        await updateAssignment(existing.id, { Status: 'Active' });
        setMessage({ type: 'success', text: `Restored access to ${app?.fields.Title}` });
      } else {
        // New assignment
        await createAssignment({
          EmployeeId: Number(selectedEmployee.id),
          AppId: appId,
          LicenseType: app?.fields.LicenseModel ?? 'Standard',
          AssignedDate: new Date().toISOString(),
          Status: 'Active',
          AssignedBy: currentUser?.email ?? '',
        });
        setMessage({ type: 'success', text: `Granted access to ${app?.fields.Title}` });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Operation failed',
      });
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>User Access</PageTitle>
        <Caption1>
          {filteredEmployees.length} active members · {apps.length} applications
        </Caption1>
      </div>

      {message && (
        <MessageBar intent={message.type}>
          <MessageBarBody>{message.text}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.layout}>
        <div className={styles.sidebar}>
          <Input
            contentBefore={<Search24Regular />}
            placeholder="Search member..."
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            style={{ marginBottom: 8, width: '100%' }}
            size="small"
          />
          <Dropdown
            placeholder="Department"
            value={deptFilter === 'all' ? 'All departments' : deptFilter}
            onOptionSelect={(_, d) => setDeptFilter(d.optionValue as string)}
            style={{ marginBottom: 8, width: '100%' }}
            size="small"
          >
            <Option value="all">All departments</Option>
            {departments.map((d) => (
              <Option key={d} value={d}>
                {d}
              </Option>
            ))}
          </Dropdown>
          <Divider style={{ margin: '8px 0' }} />
          {filteredEmployees.map((emp) => (
            <div
              key={emp.id}
              className={`${styles.memberCard} ${selectedId === emp.id ? styles.memberCardActive : ''}`}
              onClick={() => setSelectedId(emp.id)}
            >
              <Avatar
                name={emp.fields.Title}
                initials={getInitials(emp.fields.Title)}
                size={32}
                color="brand"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Body2
                  style={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {emp.fields.Title}
                </Body2>
                <Caption1>{emp.fields.Department}</Caption1>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.main}>
          {!selectedEmployee ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Body1>Select a member to view and manage their app access.</Body1>
            </div>
          ) : (
            <>
              <div className={styles.profile}>
                <Avatar
                  name={selectedEmployee.fields.Title}
                  initials={getInitials(selectedEmployee.fields.Title)}
                  size={64}
                  color="brand"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Title2>{selectedEmployee.fields.Title}</Title2>
                  <Caption1>
                    {selectedEmployee.fields.JobTitle} · {selectedEmployee.fields.Department}
                  </Caption1>
                  <Caption1 style={{ display: 'block' }}>
                    {selectedEmployee.fields.Email}
                  </Caption1>
                </div>
              </div>

              <div className={styles.summary}>
                <div className={styles.summaryCard}>
                  <Caption1>Active Apps</Caption1>
                  <div className={styles.summaryValue}>{summary.activeCount}</div>
                </div>
                <div className={styles.summaryCard}>
                  <Caption1>Monthly License Cost</Caption1>
                  <div className={styles.summaryValue}>
                    {formatCurrency(summary.totalCost)}
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <Caption1>Revoked</Caption1>
                  <div className={styles.summaryValue}>{summary.revokedCount}</div>
                </div>
                <div className={styles.summaryCard}>
                  <Caption1>Not Assigned</Caption1>
                  <div className={styles.summaryValue}>{summary.availableApps}</div>
                </div>
              </div>

              <Divider style={{ margin: '20px 0' }} />

              <TabList
                selectedValue={tab}
                onTabSelect={(_, d) => setTab(d.value as typeof tab)}
              >
                <Tab value="access">Access ({apps.length})</Tab>
                <Tab value="history" icon={<History24Regular />}>
                  History ({userHistory.length})
                </Tab>
              </TabList>

              {tab === 'access' ? (
                <div className={styles.accessGrid}>
                  {apps
                    .sort((a, b) => a.fields.Title.localeCompare(b.fields.Title))
                    .map((app) => {
                      const appId = Number(app.id);
                      const assigned = assignmentMap.has(appId);
                      const assignedItem = assignmentMap.get(appId);
                      return (
                        <div
                          key={app.id}
                          className={`${styles.appRow} ${assigned ? styles.appRowAssigned : ''}`}
                        >
                          <div className={styles.appHeader}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Tooltip content={app.fields.Title} relationship="description">
                                <Body2 className={styles.appTitle}>
                                  {app.fields.Title}
                                </Body2>
                              </Tooltip>
                              <Caption1 className={styles.appMeta}>
                                {app.fields.Vendor} · {app.fields.Category}
                              </Caption1>
                            </div>
                            <Badge
                              appearance="outline"
                              color={assigned ? 'success' : 'subtle'}
                              size="small"
                              style={{ flexShrink: 0 }}
                            >
                              {assigned ? 'Granted' : 'Not'}
                            </Badge>
                          </div>
                          <Caption1 className={styles.appMeta}>
                            {formatCurrency(app.fields.MonthlyCostPerLicense ?? 0)}/mo
                            {' · '}
                            {assignedItem?.fields.LicenseType ?? app.fields.LicenseModel}
                          </Caption1>
                          {canEdit && (
                            <Tooltip
                              content={
                                assigned
                                  ? 'Revoke this license'
                                  : 'Grant this license'
                              }
                              relationship="description"
                            >
                              <Button
                                appearance={assigned ? 'outline' : 'primary'}
                                size="small"
                                icon={
                                  updating === `${appId}` ? (
                                    <Spinner size="tiny" />
                                  ) : assigned ? (
                                    <Dismiss24Regular />
                                  ) : (
                                    <Checkmark24Regular />
                                  )
                                }
                                disabled={updating === `${appId}`}
                                onClick={() => handleToggle(appId, assigned)}
                              >
                                {assigned ? 'Revoke' : 'Grant'}
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div style={{ marginTop: 16 }}>
                  {userHistory.length === 0 ? (
                    <Caption1>No audit events for this user.</Caption1>
                  ) : (
                    userHistory.map((l) => (
                      <div key={l.id} className={styles.historyRow}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <Badge appearance="filled" size="small">
                            {l.fields.Action}
                          </Badge>
                          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                            {formatDateTime(l.fields.Timestamp)}
                          </Caption1>
                        </div>
                        <Body2>{l.fields.EntityName}</Body2>
                        <Caption1>{l.fields.Details}</Caption1>
                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                          by {l.fields.ChangedBy?.split('@')[0]}
                        </Caption1>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
